import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';

const paymentModeOptions = [
    { label: 'Cash', value: 'Cash' },
    { label: 'Online', value: 'Online' },
    { label: 'Cheque', value: 'Cheque' },
];

const CollectFeeDialog = ({ student, allocations, schoolId, financialYear, userId, onDone, onClose }) => {
    const { catchResponse } = useCatchResponse();
    const [discountPercent, setDiscountPercent] = useState(0);
    const [paymentDate, setPaymentDate] = useState(new Date());
    const [paymentMode, setPaymentMode] = useState('Cash');
    const [remarks, setRemarks] = useState('');
    const [collectedBy, setCollectedBy] = useState(userId || '');
    const [collectorList, setCollectorList] = useState([]);
    const [feeTemplateList, setFeeTemplateList] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [loading, setLoading] = useState(false);

    const subTotal = allocations.reduce((sum, a) => sum + (a.Outstanding || 0), 0);
    const fineTotal = allocations.reduce((sum, a) => sum + (a.Fine || 0), 0);
    const discountAmount = Math.round((subTotal * (discountPercent || 0)) / 100);
    const grandTotal = subTotal - discountAmount;

    useEffect(() => {
        searchCollectors('');
        fetchFeeTemplateList();
    }, []);

    const searchCollectors = async (searchText) => {
        try {
            const response = await api.post(APIConstant.path.SearchCollectors, {
                SchoolId: schoolId,
                Search: searchText || '',
                Limit: 10,
            });
            if (response.success) {
                setCollectorList(response.data?.map(u => ({
                    label: u.Name,
                    value: u._id,
                })) || []);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const fetchFeeTemplateList = async () => {
        try {
            const response = await api.post(APIConstant.path.GetFeeTemplateList, { UserID: schoolId });
            if (response.success) {
                const templates = (response.data || []).map(t => ({
                    label: t.TemplateName,
                    value: t._id,
                }));
                setFeeTemplateList(templates);
                if (templates.length > 0) {
                    setSelectedTemplate(templates[0].value);
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleSave = async () => {
        if (!paymentDate) {
            Toast.error({ message: 'Payment Date is required' });
            return;
        }

        try {
            setLoading(true);
            Loader.show();

            const paymentItems = allocations.map(a => ({
                AllocationId: a._id,
                FeeTypeName: a.FeeTypeName,
                Amount: a.Outstanding - a.Fine,
                Fine: a.Fine,
            }));

            const response = await api.post(APIConstant.path.CollectFee, {
                StudentId: student._id,
                PaymentDate: paymentDate.toISOString(),
                PaymentMode: paymentMode,
                TotalAmount: subTotal,
                DiscountPercent: discountPercent,
                DiscountAmount: discountAmount,
                NetAmount: grandTotal,
                CollectedBy: collectedBy,
                Remarks: remarks,
                PaymentItems: JSON.stringify(paymentItems),
                FeeTemplateId: selectedTemplate,
                FinancialYear: financialYear,
                SchoolId: schoolId,
                CreatedBy: userId,
            });

            if (response.success) {
                Toast.success({ message: `Fee collected successfully. Receipt No: ${response.ReceiptNo}` });
                Loader.hide();
                onDone(response.data?._id);
            } else {
                Toast.error({ message: response.message || 'Failed to collect fee' });
                Loader.hide();
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
            Loader.hide();
            catchResponse(err);
        }
    };

    return (
        <div className="collect-fee-dialog">
            <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                        <h3 style={{ margin: 0, color: '#1C515E' }}>{student.StudentName}</h3>
                        <p style={{ margin: '4px 0', color: '#666', fontSize: '13px' }}>
                            Roll No: {student.RollNo} | G.R. No: {student.GRNo}
                        </p>
                    </div>
                </div>
            </div>

            <h4 style={{ color: '#1C515E', margin: '0 0 12px 0' }}>Collect Fees</h4>

            <DataTable value={allocations} size="small" className="custom-table p-mb-3">
                <Column header="Sr No." body={(_, { rowIndex }) => rowIndex + 1} style={{ width: '8%' }} />
                <Column field="FeeTypeName" header="Particulars" style={{ width: '30%' }} />
                <Column header="Fine" body={(row) => `₹ ${row.Fine?.toLocaleString('en-IN')}`} style={{ width: '15%' }} />
                <Column header="Outstanding" body={(row) => `₹ ${(row.Outstanding - row.Fine)?.toLocaleString('en-IN')}`} style={{ width: '22%' }} />
                <Column header="Total" body={(row) => `₹ ${row.Outstanding?.toLocaleString('en-IN')}`} style={{ width: '25%' }} />
            </DataTable>

            <div style={{ background: '#f8f9fa', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Sub Total</span>
                    <span style={{ fontWeight: 600 }}>₹ {subTotal.toLocaleString('en-IN')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span>Discount (%)</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <InputText
                            value={discountPercent}
                            onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
                            style={{ width: '80px', textAlign: 'right' }}
                            maxLength={3}
                        />
                        <span style={{ fontWeight: 600, color: '#c53030' }}>- ₹ {discountAmount.toLocaleString('en-IN')}</span>
                    </div>
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, fontSize: '16px' }}>TOTAL</span>
                    <span style={{ fontWeight: 700, fontSize: '16px', color: '#1C515E' }}>₹ {grandTotal.toLocaleString('en-IN')}</span>
                </div>
            </div>

            <div className="p-grid p-mb-3">
                <div className="p-col-12 p-md-4">
                    <label style={{ fontWeight: 600, fontSize: '13px' }}>Payment Date *</label>
                    <Calendar
                        className="p-w-100 p-mt-1"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                    />
                </div>
                <div className="p-col-12 p-md-4">
                    <label style={{ fontWeight: 600, fontSize: '13px' }}>Payment Mode</label>
                    <Dropdown
                        className="p-w-100 p-mt-1"
                        value={paymentMode}
                        options={paymentModeOptions}
                        onChange={(e) => setPaymentMode(e.value)}
                    />
                </div>
                <div className="p-col-12 p-md-4">
                    <label style={{ fontWeight: 600, fontSize: '13px' }}>Collected By</label>
                    <Dropdown
                        className="p-w-100 p-mt-1"
                        value={collectedBy}
                        options={collectorList}
                        onChange={(e) => setCollectedBy(e.value)}
                        placeholder="Search Teacher/Staff"
                        filter
                        showClear
                        onFilter={(e) => searchCollectors(e.filter)}
                        emptyFilterMessage="Type to search..."
                    />
                </div>
                <div className="p-col-12 p-md-4">
                    <label style={{ fontWeight: 600, fontSize: '13px' }}>Receipt Template</label>
                    <Dropdown
                        className="p-w-100 p-mt-1"
                        value={selectedTemplate}
                        options={feeTemplateList}
                        onChange={(e) => setSelectedTemplate(e.value)}
                        placeholder="Select Template"
                        filter
                        showClear
                    />
                </div>
                <div className="p-col-12 p-mt-2">
                    <label style={{ fontWeight: 600, fontSize: '13px' }}>Remarks</label>
                    <InputTextarea
                        className="p-w-100 p-mt-1"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        rows={3}
                        placeholder="Remarks"
                    />
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button label="Cancel" className="p-button-secondary" onClick={onClose} />
                <Button label="Save" className="p-button-primary" onClick={handleSave} loading={loading} />
            </div>
        </div>
    );
};

export default CollectFeeDialog;
